import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PrototypeLayout from '../PrototypeLayout';
import { colors, font, type as ty } from '../theme/tokens';
import { heat } from '../theme/tokens';
import {
  panel, Badge, StatCard, PageHeader, PanelTitle, DemoFooter,
  Meter, Tooltip, emptyTip, TipState, Tone,
} from '../components/ui';
import { getOverview, OverviewData } from '../data/doors';
import { reportingDate, reportingTime } from '../data/fleet';
import { useFleet } from '../FleetContext';
import PeriodSwitch from '../components/PeriodSwitch';
import { PeriodSelection, defaultPeriod, indicesFor } from '../data/period';
import { useI18n } from '../i18n';

// ── Event list ───────────────────────────────────────────────────────────────

const EventItem: React.FC<{ t: 'alert' | 'warn' | 'info'; text: string; time: string }> = ({ t, text, time }) => {
  const dot = t === 'alert' ? colors.alert : t === 'warn' ? colors.warn : colors.info;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: '6px', flexShrink: 0, backgroundColor: dot }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.ink, lineHeight: 1.5 }}>
          {text}
        </Typography>
        <Typography sx={{ ...ty.meta, fontSize: '10px', color: colors.inkFaint, mt: '3px' }}>
          {time}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Train schematic ──────────────────────────────────────────────────────────

const TrainSchematic: React.FC<{ data: OverviewData }> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tip, setTip] = useState<TipState>(emptyTip);
  const { t } = useI18n();

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const ns = 'http://www.w3.org/2000/svg';
    const el = (tag: string, attrs: Record<string, string>) => {
      const e = document.createElementNS(ns, tag);
      for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
      return e;
    };

    const totalW = 640, cabW = 36, gap = 6, numCars = 4;
    const bodyW = (totalW - cabW * 2 - gap * (numCars + 1)) / numCars;
    const bodyH = 140, trainY = 50;
    const midY = trainY + bodyH / 2;
    const carStartX = gap + cabW + gap;
    const rearCabX = carStartX + bodyW * numCars + gap * numCars;
    const doorW = 38, doorH = 20, doorInset = 12, doorMargin = 14;

    const cleanup: Array<() => void> = [];

    const defs = el('defs', {});
    const marker = el('marker', { id: 'p1-arr', viewBox: '0 0 10 10', refX: '8', refY: '5', markerWidth: '6', markerHeight: '6', orient: 'auto-start-reverse' });
    marker.appendChild(el('path', { d: 'M2 1L8 5L2 9', fill: 'none', stroke: colors.inkGhost, 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }));
    defs.appendChild(marker);
    svg.appendChild(defs);

    [trainY - 18, trainY + bodyH + 18].forEach(yy => {
      svg.appendChild(el('line', { x1: '8', y1: String(yy), x2: '632', y2: String(yy), stroke: colors.border, 'stroke-width': '3', 'stroke-linecap': 'round' }));
    });
    for (let x = 18; x < 632; x += 22) {
      svg.appendChild(el('line', { x1: String(x), y1: String(trainY - 18), x2: String(x), y2: String(trainY + bodyH + 18), stroke: colors.bgAlt, 'stroke-width': '1.5', 'stroke-linecap': 'round' }));
    }

    [gap, rearCabX].forEach(x => {
      svg.appendChild(el('rect', { x: String(x), y: String(trainY), width: String(cabW), height: String(bodyH), rx: '4', fill: colors.bgAlt, stroke: colors.borderStrong, 'stroke-width': '1' }));
      const tx = el('text', { x: String(x + cabW / 2), y: String(midY), 'text-anchor': 'middle', 'dominant-baseline': 'central', 'font-size': '8', fill: colors.inkFaint, 'font-family': font.mono, 'letter-spacing': '0.08em' });
      tx.textContent = 'CAB';
      svg.appendChild(tx);
    });

    data.carriages.forEach((car, ci) => {
      const cx = carStartX + ci * (bodyW + gap);

      svg.appendChild(el('rect', { x: String(cx), y: String(trainY), width: String(bodyW), height: String(bodyH), rx: '2', fill: colors.bg, stroke: colors.border, 'stroke-width': '1' }));
      svg.appendChild(el('line', { x1: String(cx + 8), y1: String(midY), x2: String(cx + bodyW - 8), y2: String(midY), stroke: colors.border, 'stroke-width': '0.75', 'stroke-dasharray': '4 3' }));

      const clbl = el('text', { x: String(cx + bodyW / 2), y: String(midY), 'text-anchor': 'middle', 'dominant-baseline': 'central', 'font-size': '11', fill: colors.inkGhost, 'font-family': font.mono, 'font-weight': '500', 'letter-spacing': '0.06em' });
      clbl.textContent = car.id;
      svg.appendChild(clbl);

      const doorXs: Record<string, number> = {
        fwd: cx + doorMargin,
        rear: cx + bodyW - doorMargin - doorW,
      };

      car.doors.forEach(door => {
        const dx = doorXs[door.pos];
        const dy = door.side === 'A' ? trainY + doorInset : trainY + bodyH - doorInset - doorH;

        const c = heat[door.heat]!;
        const g = el('g', { style: 'cursor:pointer' });

        g.appendChild(el('rect', { x: String(dx), y: String(dy), width: String(doorW), height: String(doorH), rx: '2', fill: c.fill, stroke: c.stroke, 'stroke-width': '1.25' }));

        const txt = el('text', { x: String(dx + doorW / 2), y: String(dy + doorH / 2), 'text-anchor': 'middle', 'dominant-baseline': 'central', 'font-size': '9', fill: c.text, 'font-family': font.mono, 'font-weight': '500' });
        txt.textContent = String(door.cycles);
        g.appendChild(txt);

        if (door.events > 0) {
          g.appendChild(el('circle', { cx: String(dx + doorW - 3), cy: String(dy + 3), r: '4', fill: colors.alert, stroke: colors.bg, 'stroke-width': '1.5' }));
        }

        const content =
          `${door.label} · ${door.side === 'A' ? t('common.platform') : t('common.trackside')}\n` +
          `${t('p1.usage')}: ${t(`p1.heat.${door.heat}`)}  ·  ${t('common.cycles')}: ${door.cycles}\n` +
          `${t('p1.misuseEvent')}: ${door.events}`;

        const onEnter = (e: MouseEvent) => setTip({ visible: true, x: e.clientX + 16, y: e.clientY - 56, content });
        const onMove = (e: MouseEvent) => setTip(s => ({ ...s, x: e.clientX + 16, y: e.clientY - 56 }));
        const onLeave = () => setTip(s => ({ ...s, visible: false }));

        g.addEventListener('mouseenter', onEnter);
        g.addEventListener('mousemove', onMove);
        g.addEventListener('mouseleave', onLeave);
        cleanup.push(() => {
          g.removeEventListener('mouseenter', onEnter);
          g.removeEventListener('mousemove', onMove);
          g.removeEventListener('mouseleave', onLeave);
        });

        svg.appendChild(g);
      });
    });

    svg.appendChild(el('line', { x1: '22', y1: String(trainY - 30), x2: '78', y2: String(trainY - 30), stroke: colors.inkGhost, 'stroke-width': '1', 'marker-end': 'url(#p1-arr)' }));

    return () => { cleanup.forEach(fn => fn()); };
  }, [data, t]);

  return (
    <Box sx={{ ...panel, mb: 3 }}>
      <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mb: 1 }}>
        {t('p1.sideA')}
      </Typography>

      <svg ref={svgRef} viewBox="0 0 640 240" style={{ display: 'block', width: '100%' }} role="img">
        <title>{t('p1.schematic')}</title>
      </svg>

      <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mt: 1, mb: 2 }}>
        {t('p1.sideB')}
      </Typography>

      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 2.5,
        pt: 2, borderTop: `1px solid ${colors.border}`,
      }}>
        {[1, 2, 3, 4, 5].map(i => {
          const h = heat[i]!;
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box sx={{
                width: 14, height: 14, borderRadius: '2px',
                backgroundColor: h.fill, border: `1px solid ${h.stroke}`, flexShrink: 0,
              }} />
              <Typography sx={{ fontFamily: font.sans, fontSize: '12px', color: colors.inkMuted }}>
                {t(`p1.heat.${i}`)}
              </Typography>
            </Box>
          );
        })}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: colors.alert, flexShrink: 0 }} />
          <Typography sx={{ fontFamily: font.sans, fontSize: '12px', color: colors.inkMuted }}>
            {t('p1.misuseEvent')}
          </Typography>
        </Box>
      </Box>

      <Tooltip tip={tip} />
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const forecastTone = (pct: number): Tone =>
  pct >= 75 ? 'alert' : pct >= 50 ? 'warn' : pct >= 30 ? 'ok' : 'neutral';

const forecastNote = (pct: number) =>
  pct >= 75 ? 'p1.serviceWithin'
    : pct >= 60 ? 'p1.approachingThreshold'
    : pct >= 45 ? 'p1.slowClose'
    : pct >= 30 ? 'p1.normalWear'
    : 'p1.withinExpected';

const Prototype1: React.FC = () => {
  const { unitId, caption } = useFleet();
  const { t, locale } = useI18n();

  const [period, setPeriod] = useState<PeriodSelection>(defaultPeriod);
  const periodDays = indicesFor(period).length;
  const data = useMemo(() => getOverview(unitId, period), [unitId, period]);

  return (
    <PrototypeLayout protoId="01" titleKey="proto.01.title">
      <PageHeader
        eyebrow={`${t('p1.fleetOverview')} · ${caption}`}
        title={t('proto.01.title')}
        right={
          <>
            {data.misuseEvents > 0 && (
              <Badge label={t('p1.alerts', { n: data.highSeverity || 1 })} t="alert" />
            )}
            <Badge label={t('common.syntheticData')} t="info" />
            <Typography sx={{ ...ty.meta, color: colors.inkFaint }}>
              {reportingDate(locale)} · {reportingTime(locale)}
            </Typography>
          </>
        }
      />

      <PeriodSwitch
        sel={period}
        onChange={setPeriod}
        note={t('period.note', { days: periodDays })}
      />

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' },
        gap: 2,
        mb: 4,
      }}>
        <StatCard
          label={t('period.cycles')}
          value={data.cyclesInPeriod.toLocaleString(locale)}
          sub={t(`period.${period.key}`)}
        />
        <StatCard
          label={t('p1.misuseEvents')}
          value={String(data.misuseEvents)}
          sub={t('p1.highSeverity', { n: data.highSeverity })}
          t={data.misuseEvents > 4 ? 'alert' : data.misuseEvents > 0 ? 'warn' : 'ok'}
        />
        <StatCard
          label={t('p1.avgCycleTime')}
          value={`${data.avgCycleSeconds.toLocaleString(locale)}s`}
          sub={t('p1.withinRange')}
        />
        <StatCard
          label={t('p1.nextService')}
          value={`${data.nextServiceDays}${t('common.days')}`}
          sub={t('p1.flaggedDoor', { door: data.worstDoor })}
          t={data.nextServiceDays < 15 ? 'alert' : 'warn'}
        />
      </Box>

      <Typography sx={{ ...ty.eyebrow, color: colors.inkFaint, mb: 1.5 }}>
        {t('p1.schematic')}
      </Typography>
      <TrainSchematic data={data} />

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 3,
      }}>
        <Box sx={panel}>
          <PanelTitle right={<Badge label={t('p1.today')} t="neutral" />}>
            {t('p1.recentEvents')}
          </PanelTitle>
          {data.events.map((e, i) => (
            <EventItem
              key={i}
              t={e.tone}
              text={t(e.textKey, { door: e.door })}
              time={`${e.time} · ${e.place}`}
            />
          ))}
        </Box>

        <Box sx={panel}>
          <PanelTitle right={<Badge label={t('p1.horizon')} t="neutral" />}>
            {t('p1.maintenanceForecast')}
          </PanelTitle>
          {data.forecast.map(f => (
            <Meter
              key={f.label}
              label={f.label === 'others' ? t('p1.others') : f.label}
              pct={f.pct}
              t={forecastTone(f.pct)}
              note={t(forecastNote(f.pct))}
            />
          ))}
        </Box>
      </Box>

      <DemoFooter note={t('common.syntheticNote')} />
    </PrototypeLayout>
  );
};

export default Prototype1;
